<script lang="ts">
  import { push } from 'svelte-spa-router';
  import Table from '../../Table.svelte';
  import type { DonationPayout } from '../../../lib/api/charityServices';

  export let recentPayouts: DonationPayout[] = [];
  export let loading = false;
  export let error: string | null = null;
  export let viewAllHref = '#/payouts';
  export let showConfirmationStatus = true;

  $: columns = [
    {
      key: 'companyName',
      label: 'Company',
      width: showConfirmationStatus ? '32%' : '40%',
      mobilePrimary: true,
      render: (value: string) => `<span class="company-link">${value}</span>`
    },
    {
      key: 'total',
      label: 'Total',
      width: showConfirmationStatus ? '20%' : '25%',
      render: (value: number) => `<span class="total-value">${formatCurrency(value)}</span>`
    },
    {
      key: 'date',
      label: 'Date',
      width: showConfirmationStatus ? '20%' : '25%',
      render: (value: string) => `<span class="date-value">${formatDate(value)}</span>`
    },
    ...(showConfirmationStatus
      ? [{
          key: 'confirmationStatus',
          label: 'Confirmation Status',
          width: '28%',
          align: 'center' as const,
          render: (value: string) => {
            const statusClass = value === 'Confirmed Received' ? 'received' : 'pending-receival';
            const icon = value === 'Confirmed Received'
              ? '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>'
              : '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/><path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>';
            return `<span class="confirmation-badge ${statusClass}">${icon}<span>${value}</span></span>`;
          }
        }]
      : [])
  ];

  function handleRowClick(row: { uuid?: string }) {
    if (row.uuid) {
      push(`/payout-detail/${row.uuid}`);
    }
  }

  function formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  }

  function formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  function getConfirmationStatus(payout: DonationPayout): string {
    return payout.received || payout.charity_marked_received_at
      ? 'Confirmed Received'
      : 'Pending Receival Confirmation';
  }

  $: payoutTableData = Array.isArray(recentPayouts) ? recentPayouts.map(payout => ({
    uuid: payout.uuid,
    companyName: payout.company || payout.employer?.name || 'N/A',
    employerUuid: payout.employer?.uuid || '',
    total: payout.net_payment || payout.payment_received || payout.total_amount,
    date: payout.transaction_date,
    confirmationStatus: getConfirmationStatus(payout)
  })) : [];
</script>

{#if loading}
  <div class="loading-container">
    <div class="loading-spinner"></div>
    <p>Loading payouts...</p>
  </div>
{:else if error}
  <div class="error-container">
    <p class="error-message">{error}</p>
  </div>
{:else}
  <Table
    actionText="View All"
    actionHref={viewAllHref}
    {columns}
    data={payoutTableData}
    onRowClick={handleRowClick}
  />
{/if}

<style>
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid #f0b429;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .error-message {
    color: #dc2626;
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  :global(.total-value) {
    font-weight: 600;
    color: #111827;
  }

  :global(.date-value) {
    color: #6b7280;
  }

  :global(.company-link) {
    color: #3b82f6;
    text-decoration: none;
    font-weight: 500;
  }

  :global(.company-link:hover) {
    text-decoration: underline;
    color: #2563eb;
  }

  :global(.confirmation-badge) {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.375rem;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: normal;
  }

  :global(.confirmation-badge svg) {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  :global(.confirmation-badge.pending-receival) {
    background: #dbeafe;
    color: #1e40af;
  }

  :global(.confirmation-badge.received) {
    background: #d1fae5;
    color: #065f46;
  }
</style>
